import { RegistryContext, useAtomValue } from '@effect/atom-react';
import { getNotePlaybackTarget } from '@listening/frontend/audio-chunk-player/note-playback';
import { idleQuery } from '@listening/frontend/query/api-runtime';
import { audioRowAtom } from '@listening/frontend/query/audio-store';
import { notesAtom } from '@listening/frontend/query/notes';
import { parsePublicIdFromSlug } from '@listening/utils/slug-utils';
import { AsyncResult } from 'effect/reactivity';
import { use, useEffect, useRef } from 'react';
import {
  useAudioChunkPlayer,
  useAudioChunkSelector,
} from '@/lib/audio-chunk-player';

const slugToAudioId = parsePublicIdFromSlug;

function useNotePlaybackTarget(noteId: string | undefined) {
  // Only read while the route names a note: this page also serves public
  // readers, where the notes request is unauthorized — and without a note the
  // list isn't needed at all.
  const notes = AsyncResult.getOrElse(
    useAtomValue(noteId === undefined ? idleQuery : notesAtom),
    () => [],
  );
  if (noteId === undefined) return undefined;
  const note = notes.find((candidate) => candidate.id === noteId);
  return note ? getNotePlaybackTarget(note) : undefined;
}

/**
 * Loads the document this route names, at the note in `?note=<id>` when there
 * is one.
 *
 * The note travels with the load rather than seeking after it: a seek needs a
 * document the player has already resolved, and the load that resolves it is
 * still in flight while the reader mounts. A note that resolves late issues its
 * own load, which supersedes the first; one that never resolves — deleted,
 * unreadable, or anchorless — leaves the document at its saved progress.
 */
export function useLoadAudioFromSlug(slug: string, noteId?: string) {
  const slugAudioId = slugToAudioId(slug);
  const audioPlayerId = useAudioChunkSelector((s) => s.audioId);
  const lookupAudioId = useAudioChunkSelector((s) => s.lookupAudioId);

  const audioChunkPlayer = useAudioChunkPlayer();
  const { loadAudio, loadAudioAtNote, loadAudioFromProgress, unloadAudio } =
    audioChunkPlayer;
  const registry = use(RegistryContext);
  const noteTarget = useNotePlaybackTarget(noteId);

  const isAlreadyLoaded =
    slugAudioId === audioPlayerId || slugAudioId === lookupAudioId;
  // One load per document and one per (document, note): a load is latest-wins
  // and takes a while to reach the store, so a re-render in between must not
  // issue it twice.
  const noteRequest =
    noteId === undefined ? undefined : `${slugAudioId}|${noteId}`;
  const loadedDocument = useRef<string | undefined>(undefined);
  const loadedNote = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (noteTarget && loadedNote.current !== noteRequest) {
      loadedNote.current = noteRequest;
      loadedDocument.current = slugAudioId;
      if (!isAlreadyLoaded && audioPlayerId) unloadAudio();
      loadAudioAtNote(slugAudioId, noteTarget);
      return;
    }
    if (isAlreadyLoaded || loadedDocument.current === slugAudioId) return;
    loadedDocument.current = slugAudioId;
    if (audioPlayerId) unloadAudio();

    // A loaded row (from any list or per-id read) seeds playback from its
    // progress; a miss falls back to a fresh load.
    const cached = registry.get(audioRowAtom(slugAudioId));
    if (cached) {
      loadAudioFromProgress(cached);
    } else {
      loadAudio(slugAudioId);
    }
  }, [
    audioPlayerId,
    isAlreadyLoaded,
    loadAudio,
    loadAudioAtNote,
    loadAudioFromProgress,
    noteRequest,
    noteTarget,
    registry,
    slugAudioId,
    unloadAudio,
  ]);
}
